import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import productImage from "../../../assets/images/wine/default-wine-cover.jpg";
import { getWineBaseColor } from "../../../helpers/tools";
import { colors } from "../../../styles/variables";

const ImagesContainer = styled.div`
  width: 100%;
  height: ${(props) => (props.isDashboard ? "166px" : "")};
  object-fit: ${(props) => (props.isDashboard ? "none" : "")};
  display: flex;
  flex-flow: row wrap;
`;

const QuarterSizedImage = styled.img`
  width: 50%;
  height: 50%;
  object-fit: ${(props) => (props.isDashboard ? "cover" : "")};
  display: flex;
`;

const QuarterSizedImageWithoutCover = styled.img`
  width: 30px;
  height: 72px;
`;

const QuarterSizedBackground = styled.div`
  width: 50%;
  height: 50%;
  background-color: ${(props) => props.backgroundColor ?? colors.purpleThin};
  justify-content: center;
  display: flex;
  align-items: center;
`;

const SingleImage = styled.img`
  width: 100%;
  height: ${(props) => (props.isDashboard ? "166px" : "")};
  object-fit: ${(props) => (props.isDashboard ? "cover" : "")};
  display: flex;
`;

const SavedListItemImages = ({ wines = /** @type {any[]} */ ([]), isDashboard = false }) => {
  const numberOfWines = (wines && wines.length) || 0;

  /**
   * Renders single image for Saved Lists with 0, 1, 2 or 3 bottles.
   *
   * @returns {Component}
   * */
  const renderSingleWine = () => {
    const [wine] = wines || [];
    const { product } = wine || {};
    const { coverPhotoSmall } = product || {};
    return (
      <SingleImage src={coverPhotoSmall || productImage} isDashboard={isDashboard} alt="Single Wine Image" />
    );
  };

  /**
   * Renders four image grid for Saved Lists with 4 or more bottles.
   *
   * @returns {Component}
   * */
  const renderFourWinesGrid = () => {
    // Sort wines alphabetically
    wines.sort((a, b) => (a.product.name > b.product.name ? 1 : -1));
    const fourWines = wines.slice(0, 4); // Slices wines array to len=4 if it has more 4 bottles.
    return (
      <ImagesContainer isDashboard={isDashboard}>
        {fourWines.map((wine) => {
          const { product, wineType } = wine || {};
          const { coverPhotoSmall, productPhotos, slug } = product || {};
          let smallPhotoWithoutBackground;
          if (isDashboard && productPhotos && productPhotos.length) {
            const productPhoto = productPhotos[0];
            const { photoSmall: photoSmallWithoutBackground } = productPhoto || {};
            smallPhotoWithoutBackground = photoSmallWithoutBackground;
          }
          if (smallPhotoWithoutBackground) {
            const { wineClass } = wineType || {};
            const { id } = wineClass || 0;
            const backgroundColor = getWineBaseColor(id);
            return (
              <QuarterSizedBackground key={smallPhotoWithoutBackground} backgroundColor={backgroundColor}>
                <QuarterSizedImageWithoutCover
                  src={smallPhotoWithoutBackground}
                  alt="Wines"
                  key={slug}
                  isDashboard={isDashboard}
                />
              </QuarterSizedBackground>
            );
          }
          return (
            <QuarterSizedImage
              src={coverPhotoSmall || productImage}
              alt="Wines"
              key={slug}
              isDashboard={isDashboard}
            />
          );
        })}
      </ImagesContainer>
    );
  };

  /**
   * Renders the Image component of <SavedListItemCard>,
   * w.r.t. number of bottles in the saved list.
   *
   * @returns {Component}
   * */
  const renderSavedListItemCardImages = () => {
    switch (numberOfWines) {
      case 0:
      case 1:
      case 2:
      case 3:
        return renderSingleWine();
      case 4:
        return renderFourWinesGrid();
      default:
        return renderFourWinesGrid(); // for 4 or more wines, render 4 wine grid.
    }
  };

  return renderSavedListItemCardImages();
};

SavedListItemImages.propTypes = {
  wines: PropTypes.arrayOf(PropTypes.shape({})),
  isDashboard: PropTypes.bool,
};

export default SavedListItemImages;
